import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Link, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ChainConnector = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const blocks = [
    { id: 1, label: 'Genesis', next: 2 },
    { id: 2, label: 'Block A', next: 3 },
    { id: 3, label: 'Block B', next: 4 },
    { id: 4, label: 'Block C', next: 5 },
    { id: 5, label: 'Block D', next: null }
  ];

  const [shuffledBlocks] = useState([...blocks].sort(() => Math.random() - 0.5));

  const handleBlockClick = (block) => {
    if (selectedBlocks.length === 0 && block.id === 1) {
      setSelectedBlocks([block]);
      setScore(score + 200);
      toast.success('Correct! +200 points');
    } else if (selectedBlocks.length > 0) {
      const lastBlock = selectedBlocks[selectedBlocks.length - 1];
      if (lastBlock.next === block.id) {
        setSelectedBlocks([...selectedBlocks, block]);
        setScore(score + 200);
        toast.success('Correct! +200 points');
        
        if (block.next === null) {
          setTimeout(() => endGame(), 1500);
        }
      } else {
        toast.error('Wrong block! This doesn\'t connect.');
        setSelectedBlocks([]);
        setScore(Math.max(0, score - 100));
      }
    } else {
      toast.error('Start with the Genesis block!');
    }
  };

  const endGame = async () => {
    setGameOver(true);
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'connector',
        score: score,
        time_taken: 0
      });
      toast.success(`Chain connected! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  const isSelected = (blockId) => selectedBlocks.some(b => b.id === blockId);

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Link className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Chain Connector</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Connect blocks in the correct sequential order to build a valid blockchain. Start with Genesis and follow the chain. Each correct connection earns 200 points!
            </p>
            <Button
              onClick={() => setGameStarted(true)}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Connecting
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Chain Complete!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Total Score</div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Connect Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Game
        </Button>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-grotesk font-bold text-white">Chain Connector</h1>
          <div className="text-2xl font-mono font-bold text-primary" data-testid="game-score">{score}</div>
        </div>

        {/* Chain Display */}
        <div className="mb-8">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Your Chain</h3>
          {selectedBlocks.length === 0 ? (
            <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 text-center text-zinc-500">
              Click on the Genesis block to start building
            </div>
          ) : (
            <div className="flex items-center space-x-2 overflow-x-auto pb-4">
              {selectedBlocks.map((block, index) => (
                <React.Fragment key={block.id}>
                  <div className="bg-primary/10 border-2 border-primary rounded-xl p-6 min-w-[150px] text-center" data-testid={`chain-block-${block.id}`}>
                    <div className="text-xs font-mono uppercase tracking-wider text-primary/80 mb-1">Block {block.id}</div>
                    <div className="text-white font-semibold">{block.label}</div>
                  </div>
                  {index < selectedBlocks.length - 1 && (
                    <div className="text-primary">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="text-sm text-zinc-400 mt-3">
            Connected: {selectedBlocks.length} / {blocks.length}
          </div>
        </div>

        {/* Available Blocks */}
        <div>
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Available Blocks</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {shuffledBlocks.map((block) => (
              <button
                key={block.id}
                onClick={() => !isSelected(block.id) && handleBlockClick(block)}
                disabled={isSelected(block.id)}
                data-testid={`block-${block.id}`}
                className={`rounded-xl p-6 text-center transition-all ${
                  isSelected(block.id)
                    ? 'bg-zinc-800/30 border-2 border-zinc-700 text-zinc-600 cursor-not-allowed'
                    : 'bg-zinc-900/30 border-2 border-white/10 text-white hover:border-primary/50 hover:bg-zinc-800/50 cursor-pointer'
                }`}
              >
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Block {block.id}</div>
                <div className="font-semibold">{block.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-zinc-900/30 border border-white/10 rounded-xl p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Instructions</h3>
          <p className="text-zinc-400 text-sm">
            Click blocks in sequential order starting from Genesis (Block 1). Each block must follow the previous one in the chain.
            Wrong selection resets your chain!
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChainConnector;
